<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/20/solid";
import { VisuallyHidden } from "radix-vue";
import { Dialog } from "radix-vue/namespaced";
import { twMerge } from "tailwind-merge";
import { useRoute, useRouter } from "vue-router";

const props = defineProps<{
  contentClassName?: string;
  hideCloseButton?: boolean;
}>();

const open = defineModel<boolean>({ required: false, default: false });

const router = useRouter();
const route = useRoute();

// On close modal, remove `modal` query param from URL.
function handleUpdateOpen(isOpen: boolean) {
  if (!isOpen && route.query.modal) {
    const newQuery = { ...route.query };
    delete newQuery.modal;
    router.replace({ query: Object.keys(newQuery).length ? newQuery : undefined });
  }
}

function customInteractOutside(event: any) {
  if (props.hideCloseButton) {
    event.preventDefault();
  } else {
    const target = event.target as HTMLElement;

    // Ensure the modal doesn't close when selecting a menu option (from vue3-select-component)
    // that is teleported outside the modal (in the body).
    if (target.classList.contains("menu-option") || target.classList.contains("menu")) {
      event.preventDefault();
    }
  }
}
</script>

<template>
  <Dialog.Root v-model:open="open" @update:open="handleUpdateOpen($event)">
    <Dialog.Trigger v-if="$slots.trigger" :asChild="true">
      <slot name="trigger" />
    </Dialog.Trigger>

    <Dialog.Portal>
      <Dialog.Overlay
        class="dialog-overlay fixed inset-0 z-10 overflow-y-auto bg-gray-400/50 backdrop-blur-md"
      />

      <Dialog.Content
        :class="[
          twMerge(
            'dialog-content fixed top-1/2 left-1/2 z-10 max-h-[92svh] w-[calc(100vw-40px)] max-w-xl overflow-y-auto rounded-[20px] bg-white px-6 pt-12 pb-8 shadow-xl will-change-transform focus:outline-none',
            props.contentClassName,
          ),
        ]"
        :aria-describedby="undefined"
        @interactOutside="
          props.hideCloseButton ? $event.preventDefault() : customInteractOutside($event)
        "
      >
        <Dialog.Close :asChild="true">
          <button
            v-if="!props.hideCloseButton"
            type="button"
            class="absolute top-4 right-4 cursor-pointer"
            aria-label="Fermer la modale"
          >
            <XMarkIcon class="text-secondary size-7" aria-hidden="true" />
          </button>
        </Dialog.Close>

        <VisuallyHidden :asChild="true">
          <Dialog.Title />
        </VisuallyHidden>

        <slot name="content" />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
</template>

<style lang="css" scoped>
@keyframes overlay-show {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes content-show {
  from {
    opacity: 0;
    transform: translateY(-40%) translateX(-50%) translateZ(0) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(-50%) translateZ(0) scale(0.99);
  }
}

.dialog-overlay {
  animation: overlay-show 0.2s ease-out forwards;
}

.dialog-content {
  animation: content-show 0.2s ease-out forwards;
}
</style>
